import { scenarioLabel } from '../../lib/voice'
import type { ScenarioIndexEntry, ScenarioView } from '../../types/voice'

// The scenarios as a rail: who the person writes to and why, with how much mail sits behind each,
// whether a model has described it, and how well it self-checks. Click to read that scenario.
//
// EVERY scenario is here, not only the ones a profile revision was built for (REQ-061-AC2): a
// scenario the last build skipped still holds the person's mail, and a count they cannot open is
// the thing they complained about. A hollow dot marks a scenario whose labels are all the cue
// table's guess — it is a count, not a measurement, and the rail does not pretend otherwise.
export function ScenarioRail({ scenarios, current, onPick, counts = [] }: {
  scenarios: ScenarioView[]
  current: string
  onPick: (scenario: string) => void
  counts?: ScenarioIndexEntry[]
}) {
  const byKey = new Map(counts.map((c) => [c.scenario, c]))
  type Row = { scenario: string; audience: string; messageCount: number; view?: ScenarioView; entry?: ScenarioIndexEntry }
  const rows = new Map<string, Row>()
  for (const s of scenarios) {
    if (s.scenario === 'all') continue
    rows.set(s.scenario, { scenario: s.scenario, audience: s.audience, messageCount: byKey.get(s.scenario)?.messageCount ?? s.messageCount, view: s, entry: byKey.get(s.scenario) })
  }
  for (const c of counts) {
    if (rows.has(c.scenario)) continue
    rows.set(c.scenario, { scenario: c.scenario, audience: c.audience, messageCount: c.messageCount, entry: c })
  }

  const list = [...rows.values()]
  const max = Math.max(1, ...list.map((s) => s.messageCount))
  const groups = new Map<string, Row[]>()
  for (const s of list) groups.set(s.audience, [...(groups.get(s.audience) ?? []), s])
  const audienceTitle = (a: string) => (a === 'colleague' ? 'To colleagues' : a === 'external' ? 'To clients and contacts' : a)
  const everything = scenarios.find((s) => s.scenario === 'all')

  return (
    <nav className="scenario-rail" aria-label="Scenarios">
      <button className={`rail-item all ${current === 'all' ? 'active' : ''}`} onClick={() => onPick('all')}>
        <span className="rail-label">Everything you send</span>
        <span className="rail-count">{everything?.messageCount.toLocaleString()}</span>
      </button>
      {[...groups.entries()].map(([audience, items]) => (
        <div key={audience} className="rail-group">
          <p className="rail-title">{audienceTitle(audience)}</p>
          {items.sort((a, b) => b.messageCount - a.messageCount).map((s) => (
            <button key={s.scenario} className={`rail-item ${current === s.scenario ? 'active' : ''}`} onClick={() => onPick(s.scenario)} title={scenarioLabel(s.scenario)}>
              <span className="rail-label">{scenarioLabel(s.scenario).split(' · ').slice(-1)[0]}</span>
              <span className="rail-bar"><span style={{ width: `${(s.messageCount / max) * 100}%` }} /></span>
              <span className="rail-meta">
                <span className="rail-count">{s.messageCount.toLocaleString()}</span>
                {s.entry && s.entry.modelSorted === 0 && s.entry.messageCount > 0 && (
                  <span className="rail-dot pattern" title="Every label in here is a cue-table match on the words, not a model's reading. Sort with a model to change that." />
                )}
                {s.view?.selfCheckScore != null && <span className={`rail-dot ${s.view.selfCheckScore >= 0.72 ? 'ok' : 'low'}`} title={`self-check ${s.view.selfCheckScore.toFixed(2)}`} />}
                {s.view?.descriptor.provisional && <span className="rail-dot provisional" title="measured only" />}
              </span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  )
}
